import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Users, Store, CheckSquare, Camera, AlertTriangle } from 'lucide-react';
import { computeEventReadiness, type EventOverview } from '@peles/shared';
import { useEvent, useEventOverview } from '../../../hooks/useEvents';
import { useToggleChecklistItem } from '../../../hooks/useEventOps';
import { EventUserListView } from '../../common/EventUserListView';
import { ReadinessRing } from '../ReadinessRing';
import { TabCard } from './EventTabPanels';
import { eventTabPath } from './eventTabs';
import { GettingStartedCard } from './GettingStartedCard';
import { daysUntil, formatShortDate } from '../../../utils/eventDates';

export function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function pct(part: number, whole: number): number {
  return whole > 0 ? Math.min(100, Math.max(0, Math.round((part / whole) * 100))) : 0;
}

function Bar({ value, className = 'bg-primary-600' }: { value: number; className?: string }) {
  return (
    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden" aria-hidden>
      <div
        className={`h-full rounded-full transition-[width] duration-500 ${className}`}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

function StatTile({
  to,
  icon,
  label,
  value,
  total,
  bar,
  barClass,
  footer,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  value: ReactNode;
  total?: ReactNode;
  bar?: number;
  barClass?: string;
  footer?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="card-lift bg-white rounded-2xl border border-gray-200 p-4 flex flex-col gap-2.5 hover:border-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
    >
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span className="text-gray-400">{icon}</span>
        {label}
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {value}
        {total != null && <span className="text-base font-medium text-gray-400"> / {total}</span>}
      </div>
      {bar != null && <Bar value={bar} className={barClass} />}
      {footer && <div className="text-xs text-gray-500">{footer}</div>}
    </Link>
  );
}

function StatTiles({ eventId, overview }: { eventId: string; overview: EventOverview }) {
  const { guests, tasks } = overview;
  const rsvpParts = [
    guests.pending > 0 && `${guests.pending} pending`,
    guests.maybe > 0 && `${guests.maybe} maybe`,
    guests.declined > 0 && `${guests.declined} declined`,
  ].filter(Boolean);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatTile
        to={eventTabPath(eventId, 'guests')}
        icon={<Users size={16} />}
        label="Guests confirmed"
        value={guests.confirmed}
        total={guests.total}
        bar={pct(guests.confirmed, guests.total)}
        footer={
          rsvpParts.length > 0
            ? rsvpParts.join(' · ')
            : guests.total === 0
              ? 'No guests yet'
              : 'Everyone has answered'
        }
      />
      <StatTile
        to={eventTabPath(eventId, 'vendors')}
        icon={<Store size={16} />}
        label="Vendors"
        value={overview.vendorCount}
        footer={
          overview.vendorBalances.length > 0
            ? `${overview.vendorBalances.length} with a balance`
            : 'Nothing owed to vendors'
        }
      />
      <StatTile
        to={eventTabPath(eventId, 'tasks')}
        icon={<CheckSquare size={16} />}
        label="Tasks done"
        value={tasks.done}
        total={tasks.total}
        bar={pct(tasks.done, tasks.total)}
        barClass="bg-teal-600"
        footer={
          tasks.overdue > 0 ? (
            <span className="text-amber-700 font-semibold">{tasks.overdue} overdue</span>
          ) : tasks.total === 0 ? (
            'No task lists yet'
          ) : (
            'Nothing overdue'
          )
        }
      />
      <StatTile
        to={eventTabPath(eventId, 'files')}
        icon={<Camera size={16} />}
        label="Photos"
        value={overview.photoCount}
        footer="Files & Photos"
      />
    </div>
  );
}

function ReadinessCard({ overview }: { overview: EventOverview }) {
  const readiness = computeEventReadiness(overview);
  return (
    <TabCard title="Readiness">
      {readiness.score == null ? (
        <p className="text-sm text-gray-500">
          Add guests, tasks or expenses and this shows how far along the event is.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-5">
            <ReadinessRing score={readiness.score} size={96} />
            <dl className="flex-1 space-y-2 text-sm">
              {readiness.parts.map((p) => (
                <div key={p.key} className="flex justify-between gap-2">
                  <dt className="text-gray-500">{p.label}</dt>
                  <dd className="font-semibold text-gray-900">{Math.round(p.ratio * 100)}%</dd>
                </div>
              ))}
            </dl>
          </div>
          {readiness.weakest && (
            <p className="mt-4 text-sm text-gray-600 bg-primary-50 rounded-xl px-3 py-2.5">
              Furthest behind:{' '}
              <strong className="text-gray-900">{readiness.weakest.label.toLowerCase()}</strong>
            </p>
          )}
        </>
      )}
    </TabCard>
  );
}

function MoneyCell({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div className={`rounded-xl px-3 py-2.5 ${danger ? 'bg-red-50' : 'bg-gray-50'}`}>
      <div className={`text-xs ${danger ? 'text-red-700' : 'text-gray-500'}`}>{label}</div>
      <div className={`text-base font-bold ${danger ? 'text-red-700' : 'text-gray-900'}`}>
        {formatMoney(value)}
      </div>
    </div>
  );
}

function BudgetCard({ eventId, overview }: { eventId: string; overview: EventOverview }) {
  const { money } = overview;
  const hasBudget = money.budget != null && money.budget > 0;
  const diff = hasBudget ? money.budget! - money.totalCost : 0;
  return (
    <TabCard
      title={hasBudget ? 'Budget vs. cost' : 'Cost'}
      actions={
        hasBudget ? (
          <span
            className={`text-xs font-semibold ${diff >= 0 ? 'text-green-700' : 'text-red-700'}`}
          >
            {formatMoney(Math.abs(diff))} {diff >= 0 ? 'under' : 'over'}
          </span>
        ) : (
          <Link
            to={eventTabPath(eventId, 'money')}
            className="text-xs text-primary-700 hover:underline"
          >
            Ledger
          </Link>
        )
      }
    >
      <div className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Committed</span>
          <strong>{formatMoney(money.totalCost)}</strong>
        </div>
        {hasBudget && (
          <>
            <Bar
              value={pct(money.totalCost, money.budget!)}
              className={diff >= 0 ? 'bg-primary-600' : 'bg-red-500'}
            />
            <div className="flex justify-end text-xs text-gray-500">
              Budget {formatMoney(money.budget!)}
            </div>
          </>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2.5">
        <MoneyCell label="Paid to vendors" value={money.paidToVendors} />
        <MoneyCell label="Still owed to vendors" value={money.owedToVendors} />
        <MoneyCell label="Client paid" value={money.clientPaid} />
        <MoneyCell
          label="Client balance due"
          value={money.clientBalanceDue}
          danger={money.clientBalanceDue > 0.005}
        />
      </div>
    </TabCard>
  );
}

function dueTone(dueDate: Date | string | null): { label: string; className: string } {
  if (!dueDate) return { label: 'No date', className: 'bg-gray-100 text-gray-600' };
  // Due dates are date-only values stored as UTC midnight.
  const d = new Date(dueDate);
  const local = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const days = daysUntil(local);
  if (days < 0) return { label: `${Math.abs(days)}d late`, className: 'bg-red-100 text-red-700' };
  if (days === 0) return { label: 'Today', className: 'bg-amber-100 text-amber-800' };
  return { label: formatShortDate(local), className: 'bg-blue-50 text-blue-700' };
}

function NextUpCard({ eventId, overview }: { eventId: string; overview: EventOverview }) {
  const toggle = useToggleChecklistItem();
  const tasks = overview.tasks.upcoming;
  return (
    <TabCard
      title="Next up"
      actions={
        <Link
          to={eventTabPath(eventId, 'tasks')}
          className="text-xs text-primary-700 hover:underline"
        >
          All tasks
        </Link>
      }
    >
      {tasks.length === 0 ? (
        <p className="text-sm text-gray-500">
          {overview.tasks.total === 0 ? 'No task lists yet.' : 'Every task is done.'}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {tasks.map((task) => {
            const tone = dueTone(task.dueDate);
            return (
              <li key={task.id}>
                <label className="flex items-center gap-3 py-2.5 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={toggle.isPending}
                    // The app's mutation cache refreshes the overview afterwards.
                    onChange={() => toggle.mutate({ itemId: task.id, isDone: true })}
                  />
                  <span className="flex-1 min-w-0 truncate text-gray-800">{task.title}</span>
                  <span
                    className={`text-[11px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap ${tone.className}`}
                  >
                    {tone.label}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </TabCard>
  );
}

function VendorBalancesCard({ eventId, overview }: { eventId: string; overview: EventOverview }) {
  return (
    <TabCard
      title="Vendors with a balance"
      actions={
        <Link
          to={eventTabPath(eventId, 'vendors')}
          className="text-xs text-primary-700 hover:underline"
        >
          Vendors & Expenses
        </Link>
      }
    >
      {overview.vendorBalances.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing is owed to vendors.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {overview.vendorBalances.map((v) => (
            <li key={v.vendorId ?? v.vendorName} className="flex items-center gap-3 py-2.5 text-sm">
              <span className="w-8 h-8 rounded-lg bg-primary-50 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                {v.vendorName.slice(0, 2).toUpperCase()}
              </span>
              <span className="flex-1 min-w-0 truncate font-medium text-gray-900">
                {v.vendorName}
              </span>
              <span className="text-right">
                <span className="block font-bold text-gray-900">{formatMoney(v.owed)}</span>
                <span className="block text-xs text-gray-500">of {formatMoney(v.total)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </TabCard>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="overview-skeleton">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-2xl bg-gray-100" />
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-56 rounded-2xl bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

/** The workspace's first tab: where the event stands, at a glance. */
export function EventOverviewTab() {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: overview, isLoading, isError } = useEventOverview(eventId);
  const { data: event } = useEvent(eventId ?? '');

  if (isLoading) return <OverviewSkeleton />;
  if (isError || !overview || !eventId) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600 bg-white rounded-2xl border border-gray-200 p-5">
        <AlertTriangle size={16} className="text-amber-600" />
        The overview could not be loaded. The other tabs still work.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GettingStartedCard eventId={eventId} createdAt={event?.createdAt} overview={overview} />
      <StatTiles eventId={eventId} overview={overview} />
      <div className="grid gap-6 lg:grid-cols-3">
        <ReadinessCard overview={overview} />
        <BudgetCard eventId={eventId} overview={overview} />
        <NextUpCard eventId={eventId} overview={overview} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <VendorBalancesCard eventId={eventId} overview={overview} />
        {/* EventUserListView carries its own "Clients" heading and Add button. */}
        <TabCard id="event-clients">
          <EventUserListView eventId={eventId} />
        </TabCard>
      </div>
      {event?.notes && (
        <TabCard title="Notes">
          <p className="text-sm text-gray-700 whitespace-pre-line">{event.notes}</p>
        </TabCard>
      )}
    </div>
  );
}
